import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

interface CountUpOptions {
  /** the floor the count starts from, so no frame ever states less than the truth (e.g. "1 milhão") */
  from?: number;
  /** turns the running integer into the text on screen (unit, grouping, plural) */
  format: (value: number) => string;
  duration?: number;
}

/**
 * The design's `[data-count]` counter. The final value is what renders on the server, without
 * IntersectionObserver and under reduced motion. With motion it is armed at its start value on mount
 * (while the reveal still hides it, so the first visible frame is the start of the count) and runs
 * once, eased, when 40% of it is on screen.
 */
export function useCountUp<T extends HTMLElement>(target: number, { from = 0, format, duration = 1100 }: CountUpOptions): [RefObject<T>, string] {
  const ref = useRef<T>(null);
  const [value, setValue] = useState(target);

  useEffect(() => {
    const element = ref.current;
    const reduced = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!element || reduced || !('IntersectionObserver' in window)) return undefined;

    setValue(from);
    let frameId = 0;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          let startedAt = 0;
          const step = (timestamp: number) => {
            if (!startedAt) startedAt = timestamp;
            const progress = Math.min(1, (timestamp - startedAt) / duration);
            const eased = 1 - (1 - progress) ** 3;
            setValue(progress < 1 ? Math.round(from + (target - from) * eased) : target);
            if (progress < 1) frameId = window.requestAnimationFrame(step);
          };
          frameId = window.requestAnimationFrame(step);
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(element);

    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(frameId);
      setValue(target);
    };
  }, [target, from, duration]);

  return [ref, format(value)];
}
